import { flatMapDeep, flatten, get } from "lodash";


/**
 * разворачиваем двевовидную коллекцию.
 * дочерним элементам присваиваем свойство `parent`
 */
export type FlattenItem<T> = T & {
  parent: FlattenItem<T> & T,
  parents: T[]
}

export function flatCollection<T extends FlattenItem<{ items?: T[] }>>(collection: (T | FlattenItem<T>)[]): FlattenItem<T>[] {
  if (!collection?.length) return [];

  const children = flatten(
    collection.map(item => item.items?.map(child => ({
      ...child,
      parent: item,
      parents: [...(item.parents ?? []), item]
    })))
  ).filter(Boolean) as unknown as FlattenItem<T>[]

  return [
    ...collection as FlattenItem<T>[],
    ...flatCollection(children)
  ]
}


/**
 * Убеждаемся, что значение является массивом,
 * и если нет, то оборачиваем в него
 */
export function ensureArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}


export function compareValues(val1: any, val2: any) {
  if (Array.isArray(val1) && Array.isArray(val2)) {
    if (val1.length !== val2.length) { return false }

    return val1.every((val, index) => val2[index] === val)
  }

  return val1 === val2
}


export function arrayIncludesValue<T>(array: T | T[], value: T) {
  const compareArray = ensureArray(array)
  return ensureArray(value).every(val => compareArray.indexOf(val) >= 0)
}


export function uniqueByPropertyName<T extends { [key: string]: any }>(array: T[], propertyName: string) {
  return array.filter((e, i) => array.findIndex(a => a[propertyName] === e[propertyName]) === i)
}


export function concat<T>(...arrays: T[][]): T[] {
  return arrays.reduce((res, cur) => [...res, ...cur], []);
}


type ObjectMapByKey<T> = { [key: string]: T };
export function objectMapByKey<T>(object: ObjectMapByKey<T>, keys: string[] = [], map: (value: T) => T): ObjectMapByKey<T> {
  const newObj: ObjectMapByKey<T> = { ...object };
  keys.forEach((key) => {
    if (newObj.hasOwnProperty(key)) {
      newObj[key] = map(newObj[key]);
    }
  });
  return newObj;
}


type ObjectWithValues = { [key: string]: any };
export function objectWithValues(object: ObjectWithValues, keys: string[]): ObjectWithValues {
  return Object.keys(object)
    .filter((key) => keys.includes(key) && object[key] !== null && object[key] !== undefined)
    .reduce((prev, next) => ({ ...prev, [next]: object[next] }), {});
}


export function enumValues<T>(enumObj: T) {
  return Object.values(enumObj).filter(value => !isNaN(value))
}


export function arrayTrimEnd<T>(arr: T[]): T[] {
  if (!Array.isArray(arr)) return arr;
  let foundNonEmpty = false;

  return arr.reduceRight((acc: T[], curr) => {
    if (curr) { foundNonEmpty = true; }
    return foundNonEmpty ? [curr, ...acc] : acc;
  }, []);
}

export function array_pad<T>(input: T[], pad_size: number, pad_value: T) {
  let pad: T[] = []
  const newArray = []
  let newLength
  let i = 0
  if (input instanceof Array && !isNaN(pad_size)) {
    newLength = ((pad_size < 0) ? (pad_size * -1) : pad_size)
    if (newLength > input.length) {
      for (i = 0; i < (newLength - input.length); i++) { newArray[i] = pad_value }
      pad = ((pad_size < 0) ? newArray.concat(input) : input.concat(newArray))
    } else {
      pad = input
    }
  }

  return pad
}

interface CollectAllValuesDataItem {
  value: any;
  items?: CollectAllValuesDataItem[];
}
export function collectAllValues(array: CollectAllValuesDataItem[], options: { valueKey?: string; itemsKey?: string } = {}): any[] {
  const { valueKey = 'value', itemsKey = 'items' } = options;

  return flatMapDeep(array, (item) => {
    const value = get(item, valueKey, []);
    const subItems = get(item, itemsKey, []);

    if (Array.isArray(value)) {
      return [...value, ...collectAllValues(subItems, options)];
    } else {
      return [value, ...collectAllValues(subItems, options)];
    }
  });
}
